// The complete-case-set loader — the SINGLE WRITER of SLICES.PATIENTS, shared
// by BOTH the Board (src/views/board.js) and the Patients registry. Ports the
// legacy loadAllCases / _loadAllCasesInner exactly (v20 lines ~1707-1735):
//
//  - fetchAll() chunk-loops .range() in PT_CHUNK-sized pages so the full set is
//    retrieved PAST the PostgREST 1,000-row cap — the healthcheck's flagged bug
//    was a SECOND, capped writer of the patients cache; there is exactly ONE
//    writer here and every consumer (board, registry, counts) reads its slice;
//  - flight().share() = legacy window._casesInflight: concurrent callers (board
//    + registry inside one realtime debounce batch, or nav racing a realtime
//    reload) share ONE in-flight fetch, so the second caller can't cancel the
//    first and leave a view stranded on skeletons;
//  - flight().begin()/isStale() = legacy ptLoadEpoch: the latest load wins —
//    after the await, a superseded load abandons WITHOUT committing (a stale
//    load must never land old rows under a fresher one);
//  - the owner join `owner:app_users(full_name)` is preserved verbatim so the
//    registry can name owners without a second query.
//
// A FAILED load sets loadError on the slice and NEVER renders a false all-clear
// (the board/registry each show their own error state). The AE-flag map and the
// pure board helpers (stage grouping, per-stage SLA badges) live here too so the
// board view stays presentational and the classification stays unit-tested.

import { store, SLICES, flight } from '../state/store.js';
import { getClient, fetchAll, applyProgrammeFilter, ok, fail } from '../db/client.js';
import {
  TABLES,
  RPCS,
  STAGES,
  STAGE_ENUM_BY_KEY,
  resolveStage,
  isClosedCase,
  AE_OPEN_STATUSES,
  AE_SERIOUSNESS,
  ARCHIVE_REASONS,
} from '../db/contract.js';
import { logAudit, AUDIT_EVENT } from './audit.js';

// Single-flight + latest-wins guard shared by every entry point (legacy
// window._casesInflight + ptLoadEpoch as ONE flight instance).
const casesFlight = flight();

/** Test-only: reset the shared flight guard. @returns {void} */
export function _resetCases() {
  // A fresh flight() drops any in-flight promise + epoch so tests don't leak.
  Object.assign(casesFlight, flight());
}

// ─── Column bridge (legacy normalizeCase, v20:1472) ────────────────────────

/**
 * Bridge the NEW DB column names to the display fields every render function
 * reads. The database stores `program_status` / `current_stage` /
 * `treating_facility` / `risk_bucket`; the ported display code reads
 * `c.status` / `c.facility_name` / `c.risk_level`. Legacy applied this to
 * EVERY page of the chunk loop before pushing — without it the High-risk
 * indicator is dead (risk_bucket never becomes risk_level) and resolveStage's
 * substring fallback has no `status` to fall back to.
 *
 * MUTATES and returns the row (exact legacy semantics — the loader owns these
 * objects and every consumer must see the same bridged shape).
 * @param {any} c a raw cases row
 * @returns {any} the same row, bridged
 */
export function normalizeCase(c) {
  if (!c) return c;
  // current_stage (the case_stage enum) → c.status (resolveStage's fallback)
  if (c.current_stage && !c.status) c.status = c.current_stage;
  // treating_facility → facility_name (display)
  if (c.treating_facility && !c.facility_name) c.facility_name = c.treating_facility;
  // risk_bucket (standard/elevated/high/critical) → risk_level (Standard/High)
  if (c.risk_bucket && !c.risk_level) {
    c.risk_level = c.risk_bucket === 'high' || c.risk_bucket === 'critical' ? 'High' : 'Standard';
  }
  c.is_archived = !!c.archived_at;
  return c;
}

/**
 * normalizeCase over a list (legacy normalizeCases). @param {any[]|null|undefined} arr @returns {any[]}
 */
export function normalizeCases(arr) {
  return (arr || []).map(normalizeCase);
}

// ─── Pure board classification (unit-tested without a DB) ──────────────────

// Per-stage SLA target in days (legacy loadPipeline slaMap). Keyed by the
// STAGES display key; anything unmapped falls back to 14 (legacy default).
export const STAGE_SLA = Object.freeze({
  triage: 3,
  enrolled: 2,
  welcome: 3,
  sha_pending: 14,
  sha_approved: 5,
  delivery: 7,
  active: 30,
  refill: 7,
});

/**
 * SLA target (days) for a stage key. Exact legacy fallback of 14.
 * @param {string} stageKey
 * @returns {number}
 */
export function slaFor(stageKey) {
  return Object.prototype.hasOwnProperty.call(STAGE_SLA, stageKey) ? STAGE_SLA[stageKey] : 14;
}

/**
 * Whole days elapsed since a timestamp (floor). A missing/invalid date is 0
 * days (a just-created card, not an "overdue" one).
 * @param {string|null|undefined} createdAt ISO timestamp
 * @param {Date} [now]
 * @returns {number}
 */
export function daysSince(createdAt, now) {
  const base = now ? now.getTime() : Date.now();
  const t = createdAt ? new Date(createdAt).getTime() : NaN;
  if (isNaN(t)) return 0;
  return Math.floor((base - t) / 86400000);
}

/**
 * The board card's SLA badge classification (legacy loadPipeline slaBadge).
 *   daysLeft < 0  → 'over'  ("Nd overdue")
 *   daysLeft <= 2 → 'warn'  ("Nd left")
 *   otherwise     → 'age'   ("Nd" — neutral age, no urgency)
 * Pure — returns the class + label + the raw numbers for the caller.
 * @param {string|null|undefined} createdAt
 * @param {string} stageKey
 * @param {Date} [now]
 * @returns {{cls: 'over'|'warn'|'age', label: string, days: number, daysLeft: number}}
 */
export function slaBadge(createdAt, stageKey, now) {
  const days = daysSince(createdAt, now);
  const daysLeft = slaFor(stageKey) - days;
  if (daysLeft < 0) return { cls: 'over', label: Math.abs(daysLeft) + 'd overdue', days, daysLeft };
  if (daysLeft <= 2) return { cls: 'warn', label: daysLeft + 'd left', days, daysLeft };
  return { cls: 'age', label: days + 'd', days, daysLeft };
}

/**
 * Bucket a case list into the 8 active board columns (legacy loadPipeline
 * grouping). Closed/archived/deceased cases are SKIPPED — they'd otherwise fall
 * through resolveStage's substring default to index 1 (Enrolled) and inflate
 * the live board + its counts. Pure — no scope filtering here (the view applies
 * scope first); returns per-key arrays (STAGES order) + counts.
 * @param {any[]} cases already scope-filtered case rows
 * @returns {{groups: Record<string, any[]>, counts: Record<string, number>, total: number}}
 */
export function groupByStage(cases) {
  /** @type {Record<string, any[]>} */
  const groups = {};
  /** @type {Record<string, number>} */
  const counts = {};
  for (const s of STAGES) {
    groups[s.key] = [];
    counts[s.key] = 0;
  }
  let total = 0;
  for (const c of cases || []) {
    if (isClosedCase(c)) continue;
    const key = STAGES[resolveStage(c)].key;
    groups[key].push(c);
    counts[key]++;
    total++;
  }
  return { groups, counts, total };
}

/**
 * SHA-status display metadata for the board chip (legacy shaStatusMeta). Pure.
 *   approved/active → ok · submitted → warn · rejected → danger · else → muted
 * @param {string|null|undefined} status cases.sha_status
 * @returns {{cls: 'ok'|'warn'|'danger'|'muted', label: string, empty: boolean}}
 */
export function shaMeta(status) {
  const s = String(status || '').toLowerCase();
  const empty = !s || s === 'not_started';
  let cls = /** @type {'ok'|'warn'|'danger'|'muted'} */ ('muted');
  if (s === 'approved' || s === 'active') cls = 'ok';
  else if (s === 'submitted') cls = 'warn';
  else if (s === 'rejected') cls = 'danger';
  const label = s ? s.replace(/_/g, ' ').replace(/^\w/, (m) => m.toUpperCase()) : 'Not started';
  return { cls, label, empty };
}

// ─── AE flags (legacy loadAEFlags / caseAEFlags) ───────────────────────────

/**
 * Reduce open adverse-event rows to a per-case flag map (legacy loadAEFlags):
 * case_id -> {hasOpenAE, isSeriousAE}. Pure — tested directly.
 * @param {any[]} aeRows adverse_events rows (case_id, seriousness)
 * @returns {Record<string, {hasOpenAE: boolean, isSeriousAE: boolean}>}
 */
export function aeFlagsFrom(aeRows) {
  /** @type {Record<string, {hasOpenAE: boolean, isSeriousAE: boolean}>} */
  const map = {};
  for (const a of aeRows || []) {
    if (!a || !a.case_id) continue;
    const f = map[a.case_id] || (map[a.case_id] = { hasOpenAE: false, isSeriousAE: false });
    f.hasOpenAE = true;
    if (a.seriousness === AE_SERIOUSNESS.SERIOUS) f.isSeriousAE = true;
  }
  return map;
}

/**
 * Load the open-AE flag map into the PATIENTS slice (merged, so it coexists
 * with the case set). Never throws.
 *
 * HONESTY (never a false all-clear): supabase-js RESOLVES on a PostgREST error
 * rather than throwing, so the error MUST be destructured — legacy swallowed it
 * and reported success, which on a serious-AE board means every card silently
 * loses its "Serious AE" chip. On failure we set `aeFlagsError` and KEEP the
 * previous flag map (a transient error must not blank chips that were correct a
 * moment ago); the board renders a degraded-state notice off that flag.
 * Board calls this before loadAllCases (legacy order).
 * @param {{client?: any}} [deps]
 * @returns {Promise<{ok: boolean}>}
 */
export async function loadAEFlags(deps) {
  const client = (deps && deps.client) || getClient();
  try {
    const { data, error } = await client
      .from(TABLES.ADVERSE_EVENTS)
      .select('case_id,seriousness,status')
      .in('status', AE_OPEN_STATUSES);
    if (error) {
      // Keep any prior flags; mark the map as untrustworthy so the view says so.
      store.set(SLICES.PATIENTS, { aeFlagsError: error });
      return fail(error);
    }
    store.set(SLICES.PATIENTS, { aeFlags: aeFlagsFrom(data || []), aeFlagsError: null });
    return ok(true);
  } catch (e) {
    console.warn('loadAEFlags', e);
    store.set(SLICES.PATIENTS, { aeFlagsError: e });
    return fail(e);
  }
}

/** True when the last AE-flag read failed — chips may be missing. @returns {boolean} */
export function aeFlagsStale() {
  return !!(store.get(SLICES.PATIENTS) || {}).aeFlagsError;
}

/** The AE flag map currently in the store (empty until loadAEFlags runs). @returns {Record<string, any>} */
export function aeFlags() {
  return (store.get(SLICES.PATIENTS) || {}).aeFlags || {};
}

// ─── The single complete-set loader (legacy loadAllCases) ──────────────────

/**
 * Load the COMPLETE case set (past the 1,000-row cap) and commit it to
 * SLICES.PATIENTS — the ONE writer both the board and registry read. Concurrent
 * callers share one fetch (flight.share); a superseded load abandons without
 * committing (flight.begin/isStale). Never throws.
 *
 * Slice shape (merged): { cases, total, loaded, loadError, aeFlags }.
 * `loaded` marks that a load has RESOLVED at least once — without it a view
 * cannot tell "still loading" from "genuinely empty" and paints a false
 * "No patients" over an in-flight fetch.
 *
 * Returns the envelope: ok(cases)+total on commit; fail(error) on a query error;
 * { stale: true } (ok:false, code:'STALE') when a newer load superseded this one.
 * @param {{client?: any}} [deps]
 * @returns {Promise<any>}
 */
export function loadAllCases(deps) {
  return casesFlight.share(() => _loadAllCasesInner(deps));
}

/**
 * READ-AFTER-WRITE reload: guaranteed to start a fetch that BEGINS after this
 * call, so it can never be satisfied by a snapshot taken before the write.
 *
 * loadAllCases() is single-flight — a caller arriving mid-fetch joins the
 * in-progress promise, whose rows predate the write. That is correct for nav
 * and realtime, but WRONG for a verified reload (set-stage confirmation, the
 * post-bulk-assign refresh): it would verify against pre-write rows and either
 * falsely warn "the board didn't confirm" on a clean save, or render stale
 * owners. Here we await whatever is in flight (so we don't stack fetches) and
 * only THEN start a fresh one; begin()/isStale() keeps latest-wins safe.
 * @param {{client?: any}} [deps]
 * @returns {Promise<any>}
 */
export async function forceReloadAllCases(deps) {
  // Drain any in-flight read first — its result is a pre-write snapshot we
  // deliberately discard (the failure of a superseded load is not ours).
  try {
    await casesFlight.share(() => Promise.resolve(null));
  } catch {
    /* a superseded load's rejection is not this caller's problem */
  }
  return casesFlight.share(() => _loadAllCasesInner(deps));
}

/**
 * @param {{client?: any}} [deps]
 * @returns {Promise<any>}
 */
async function _loadAllCasesInner(deps) {
  const client = (deps && deps.client) || getClient();
  const isStale = casesFlight.begin(); // claim this load's epoch (latest wins)
  const prog = store.get(SLICES.PROGRAMMES) || {};
  const active = (store.get(SLICES.FILTERS) || {}).programme || 'all';

  const res = await fetchAll(
    () =>
      applyProgrammeFilter(
        client
          .from(TABLES.CASES)
          .select('*,owner:app_users(full_name)', { count: 'exact' })
          // SOFT-DELETED CASES ARE EXCLUDED HERE, not left to RLS. The cases_read
          // policy reads `deleted_at is null OR is_admin()`, which is right for
          // recovery — an admin must be able to pull a wrongly-removed record back
          // — but it made the soft delete invisible to the people most likely to
          // rely on it: an admin still saw every removed patient in the registry,
          // and "Total enrolled" still counted them, because that tile counts
          // every case row in view regardless of status. Deleting a test patient
          // therefore did nothing at all for the founder. The row stays in the
          // database and one UPDATE brings it back; it just stops being treated
          // as a live patient by every surface that reads this loader.
          .is('deleted_at', null),
        'programme_id',
        active,
        prog.byCode || {}
      ).order('created_at', { ascending: false }),
    // Legacy checked the epoch INSIDE the chunk loop (v20:1722): a superseded
    // load bails at the next page boundary instead of paging a whole >1000-row
    // set it will then throw away. The post-loop check below stays as the
    // commit-time backstop.
    { shouldAbort: isStale }
  );

  if (isStale() || res.stale) return { ok: false, data: null, error: null, code: 'STALE', stale: true };

  if (!res.ok) {
    // Surface the failure (never a false all-clear); keep any prior cases so a
    // transient error doesn't blank a working board (legacy left cache intact).
    // `loaded` still flips — the load RESOLVED, so views must stop showing
    // skeletons and show the error state instead.
    store.set(SLICES.PATIENTS, { loadError: res.error, loaded: true });
    return fail(res.error);
  }

  // normalizeCases bridges the new DB columns (risk_bucket/current_stage/
  // treating_facility) to the display fields at the SINGLE commit point, so
  // every consumer — board, registry, workspace, CSV, counts — sees the bridged
  // shape exactly once (legacy normalized every page inside the loop).
  store.set(SLICES.PATIENTS, {
    cases: normalizeCases(res.data),
    total: res.total,
    loadError: null,
    loaded: true,
  });
  return Object.assign(ok(res.data), { total: res.total });
}

/** The complete case set currently in the store. @returns {any[]} */
export function allCases() {
  return (store.get(SLICES.PATIENTS) || {}).cases || [];
}

/**
 * True once a case load has RESOLVED at least once this session (success OR
 * failure). Views must NOT paint an empty state while this is false — an
 * in-flight first load is indistinguishable from a genuinely empty set
 * otherwise, and an all-zero patient board is alarming to a clinician.
 * @returns {boolean}
 */
export function casesLoaded() {
  return !!(store.get(SLICES.PATIENTS) || {}).loaded;
}

// ─── Set stage (legacy execSetStage's write half — RPC UNTOUCHED) ──────────

/**
 * Move a case to a new pipeline stage via rpc_set_stage — called BYTE-IDENTICALLY
 * to v_20 (`p_case_id`, `p_stage`, `p_reason`), the single atomic server call
 * that owns the enum sync, stage-history row, audit entry and stage task. The
 * display key is mapped back to its case_stage enum through STAGE_ENUM_BY_KEY
 * (the inverse the round-trip invariant guarantees). Returns the envelope; the
 * caller does a verified reload and keeps the modal open on failure.
 * @param {string} caseId
 * @param {string} stageKey a STAGES display key
 * @param {string} reason free-text reason (defaults to legacy 'Manual stage change')
 * @param {{client?: any}} [deps]
 * @returns {Promise<{ok: boolean, data: any, error: any, code: string|null}>}
 */
export async function setStage(caseId, stageKey, reason, deps) {
  const client = (deps && deps.client) || getClient();
  const stageEnum = STAGE_ENUM_BY_KEY[stageKey] || 'triage';
  const { error } = await client.rpc(RPCS.SET_STAGE, {
    p_case_id: caseId,
    p_stage: stageEnum,
    p_reason: (reason && String(reason).trim()) || 'Manual stage change',
  });
  if (error) return fail(error);
  // Legacy-migration reactivation: a register patient imported on hold
  // ("Inactive" sub-queue) who gets re-routed is by definition being worked
  // again — lift the hold in the same gesture so the nurse never needs a
  // second bookkeeping step. Guarded update (only rows still on_hold);
  // best-effort by design: the stage change ALREADY succeeded, and a hold
  // that stays on is visible in the Legacy view rather than silently wrong.
  try {
    await client
      .from(TABLES.CASES)
      .update({ program_status: 'active' })
      .eq('case_id', caseId)
      .eq('program_status', 'on_hold');
  } catch {
    /* non-fatal — see above */
  }
  return ok({ caseId, stageKey, stageEnum });
}

// ─── Archive / close a case (R-024 outcome capture at closure) ─────────────

/**
 * Suggest the clinical OUTCOME code (CLOSURE_OUTCOMES) implied by an
 * archive_reason value — so the modal can pre-fill the outcome when a reason is
 * picked while staying user-overridable. Pure (unit-tested). Any unmapped reason
 * falls back to 'other'.
 * @param {string} reasonValue an ARCHIVE_REASONS value
 * @returns {string} a CLOSURE_OUTCOMES v
 */
export function suggestOutcome(reasonValue) {
  switch (reasonValue) {
    case 'deceased':
      return 'died';
    case 'treatment_completed':
      return 'completed';
    case 'side_effects_severe':
      return 'discontinued_adverse';
    case 'doctor_advised_stop':
    case 'treatment_failure':
      return 'discontinued_clinical';
    case 'medication_switched_off_program':
    case 'transferred_to_another_program':
      return 'transferred';
    case 'withdrew_consent':
    case 'patient_request_financial':
    case 'patient_request_personal':
    case 'moved_out_of_area':
      return 'withdrew';
    case 'lost_to_follow_up':
    case 'unreachable_extended':
    case 'non_adherence_severe':
      return 'lost_to_follow_up';
    case 'sha_denied_permanent':
      return 'not_reimbursed';
    default:
      return 'other';
  }
}

/**
 * Interim outcome encoding (R-024). There is NO cases.outcome column yet — a
 * dedicated column + backfill is deferred to M5. Until then the structured
 * clinical outcome is folded into the archive NOTES with a machine-parseable
 * prefix, so the record is captured today and an M5 migration can lift it into
 * its own column by regex. The archive_reason_code (p_reason) is unchanged and
 * byte-identical to v_20; only the free-text p_notes carries the extra tag, so
 * the RPC contract itself is untouched. Pure (unit-tested).
 * @param {string|null|undefined} outcome a CLOSURE_OUTCOMES v (or blank)
 * @param {string|null|undefined} notes the nurse's free-text notes
 * @returns {string}
 */
export function encodeArchiveNotes(outcome, notes) {
  const body = (notes === null || notes === undefined ? '' : String(notes)).trim();
  const code = (outcome === null || outcome === undefined ? '' : String(outcome)).trim();
  if (!code) return body;
  // DEFERRED-M5: replace this notes-prefix encoding with a real cases.outcome
  // column once the schema lands. The tag is parsed back as /^\[outcome:(\w+)\]/.
  return `[outcome:${code}] ${body}`.trim();
}

/**
 * Archive / close a case (legacy execArchiveCase; R-024). Calls rpc_archive_case
 * BYTE-IDENTICALLY to v_20 (`p_case_id`, `p_reason`, `p_notes`, `p_archive_date`)
 * — the single atomic server call that closes the case, cancels open tasks,
 * writes stage history and sets the closed_* stage. The outcome is interim-encoded
 * into p_notes (encodeArchiveNotes; the archive_reason_code stays unchanged).
 * On success writes a case_archived audit row (retry-queued). Returns the
 * envelope; the caller keeps the modal open on failure.
 * @param {string} caseId
 * @param {{reason: string, outcome?: string, notes: string, date: string}} entry
 * @param {{client?: any, session?: any}} [deps]
 * @returns {Promise<{ok: boolean, data: any, error: any, code: string|null}>}
 */
export async function archiveCase(caseId, entry, deps) {
  const client = (deps && deps.client) || getClient();
  const reason = entry && entry.reason;
  const notes = encodeArchiveNotes(entry && entry.outcome, entry && entry.notes);
  const { error } = await client.rpc(RPCS.ARCHIVE_CASE, {
    p_case_id: caseId,
    p_reason: reason,
    p_notes: notes,
    p_archive_date: (entry && entry.date) || new Date().toISOString().slice(0, 10),
  });
  if (error) return fail(error);
  const rDef = ARCHIVE_REASONS.find((r) => r.value === reason);
  const outcome = (entry && entry.outcome) || null;
  await logAudit(
    AUDIT_EVENT.CASE_ARCHIVED,
    `archived case — ${rDef ? rDef.label : reason}${outcome ? ` (outcome: ${outcome})` : ''}`,
    caseId,
    { reason, outcome, leads_to: rDef ? rDef.leads_to : null },
    deps
  );
  return ok({ caseId, reason, outcome });
}
